import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { LedgerEntryRow } from "@/modules/wallet/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { Loader } from "@/shared/ui/Loader";
import { Button } from "@/components/ui/button";


import { useNavigate, useSearch } from "@tanstack/react-router";
import { BookOpenIcon, FileDownIcon } from "lucide-react";
import { useState } from "react";
import {
  useLedgerQuery,
  useLedgerSummaryQuery,
} from "../api/queries";
import { walletApi } from "../api/client";
import { LedgerFilter } from "../ui/LedgerFilter";
import { LedgerTable } from "../ui/LedgerTable";
import { LedgerSummaryCards } from "../ui/LedgerSummaryCards";

type LedgerSearch = {
  walletId?: string;
  search?: string;
  createdFrom?: string;
  createdTo?: string;
  type?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

function exportLedgerToCsv(items: LedgerEntryRow[]) {
  const headers = [
    "ວັນທີ",
    "ປະເພດ",
    "ທິດ",
    "ຍອດ (LAK)",
    "ຍອດຄົງເຫຼືອ (LAK)",
    "ອໍເດີ",
    "ລູກຄ້າ",
    "ເບີ",
    "ສະຖານະ",
  ];
  const rows = items.map((r) => [
    formatDateTimeLocal(r.createdAt),
    r.type,
    r.direction,
    String(r.amountLak),
    String(r.balanceAfterLak),
    r.orderNo ?? "",
    r.customerName ?? "",
    r.customerPhone,
    r.status,
  ]);
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    ),
  ].join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ledger-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function LedgerExplorerPage() {
  const nav = useNavigate({ from: "/app/wallet/ledger" });
  const search = useSearch({ from: "/app/wallet/ledger" }) as LedgerSearch;

  const listParams = {
    walletId: search.walletId,
    search: search.search,
    createdFrom: search.createdFrom,
    createdTo: search.createdTo,
    type: search.type,
    sortField: (search.sortField ?? "createdAt") as "createdAt",
    sortOrder: (search.sortOrder ?? "desc") as "asc" | "desc",
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const summaryParams = {
    walletId: search.walletId,
    search: search.search,
    createdFrom: search.createdFrom,
    createdTo: search.createdTo,
    type: search.type,
  };

  const { data, isLoading } = useLedgerQuery(listParams);
  const { data: summaryData, isLoading: summaryLoading } =
    useLedgerSummaryQuery(summaryParams);

  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await walletApi.getLedgerExport(summaryParams);
      const exportItems = result.items ?? [];
      if (exportItems.length === 0) {
        toast.info("ບໍ່ມີຂໍ້ມູນສົ່ງອອກ");
        return;
      }
      exportLedgerToCsv(exportItems);
      toast.success(
        `ສົ່ງອອກ ${exportItems.length.toLocaleString("lo-LA")} ລາຍການແລ້ວ`,
      );
    } catch {
      toast.error("ສົ່ງອອກລາຍງານບໍ່ສຳເລັດ");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "Ledger Explorer",
              to: "/app/wallet/ledger",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <BookOpenIcon className="size-5" />
              Ledger Explorer
            </h2>
            <p className="text-muted-foreground">
              ດູ audit trail ຂອງທຸກທັນສະເກັດ ແລະ ຍອດຄົງເຫຼືອ.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
          >
            <FileDownIcon className="size-4" />
            {isExporting ? "ກຳລັງສົ່ງອອກ…" : "ສົ່ງອອກລາຍງານ (CSV)"}
          </Button>
        </div>

        <div className="mb-4">
          <LedgerSummaryCards
            totalCredit={summaryData?.totalCredit ?? 0}
            totalDebit={summaryData?.totalDebit ?? 0}
            auditCoverage={summaryData?.auditCoverage ?? 0}
            netBalance={summaryData?.netBalance ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <LedgerFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <LedgerTable
              data={items}
              isLoading={isLoading}
              offset={listParams.offset}
              limit={listParams.limit}
              totalCount={totalCount}
              sortBy={listParams.sortField}
              sortOrder={listParams.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
            />
          )}
        </div>
      </Main>
    </>
  );
}
